import { createFileRoute } from '@tanstack/react-router';
import { CodeBlock, DocTable, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/interlucent-uploader')({
  component: InterlucentUploaderPage,
});

function InterlucentUploaderPage() {
  return (
    <>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Interlucent Uploader</h1>
      <p className={styles.pageSubtitle}>
        Upload application versions to Interlucent from your terminal or CI/CD
        pipeline. Optimized for large files (5–50 GB) with multipart upload,
        automatic resume, and real-time progress.
      </p>

      <section className={styles.section}>
        <p className={styles.prose}>
          The Interlucent Uploader is a cross-platform CLI tool that uploads packaged
          application versions (zip files) to the Interlucent platform. It
          authenticates with a project secret key, connects to the gateway via
          WebSocket, and streams your file to R2 storage using concurrent
          multipart upload.
        </p>
        <p className={styles.prose}>
          This is useful in two main scenarios:
        </p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>CI/CD Pipelines</span>{' '}
            Automate version deployments from GitHub Actions, GitLab CI, or any
            other pipeline. The CLI is designed for non-interactive use with{' '}
            <code className={styles.mono}>--yes</code> and{' '}
            <code className={styles.mono}>--silent</code> flags.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Manual Uploads</span>{' '}
            Upload large builds from your workstation with a progress bar,
            automatic retry on network errors, and resume support for
            interrupted uploads.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="how-it-works">
          How It Works
        </h2>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              Authenticates using your project secret key (
              <code className={styles.mono}>sk-il-...</code>) to obtain a
              short-lived access token.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              Connects to the gateway via WebSocket and receives a snapshot of
              your project's applications and versions.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              Initiates a multipart upload, streaming your zip file in parallel
              chunks directly to R2 via presigned URLs.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              On completion, the new version is created and becomes visible in
              the console immediately.
            </span>
          </li>
        </ol>
        <InfoCallout variant="info">
          Console users connected to the same organization see upload progress
          in real time — no polling required. The CLI and console share the same
          WebSocket infrastructure.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="installation">
          Installation
        </h2>

        <h3 className={styles.subsectionTitle} id="install-linux-macos">
          Linux / macOS
        </h3>
        <CodeBlock language="bash">
          {`curl -fsSL https://cdn.interlucent.ai/tools/tools.sh | bash`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="install-windows">
          Windows (PowerShell)
        </h3>
        <CodeBlock language="powershell">
          {`irm https://cdn.interlucent.ai/tools/tools.ps1 | iex`}
        </CodeBlock>
        <p className={styles.prose}>
          This installs the{' '}
          <code className={styles.mono}>interlucent-uploader</code> binary and makes it
          available on your PATH.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="quick-start">
          Quick Start
        </h2>

        <h3 className={styles.subsectionTitle} id="get-secret-key">
          1. Get a project secret key
        </h3>
        <p className={styles.prose}>
          You'll need a project secret key (
          <code className={styles.mono}>sk-il-...</code>) from the Interlucent
          dashboard. Navigate to your project settings to generate one.
        </p>
        <p className={styles.prose}>
          Store the key in an environment variable so you don't have to paste it
          into every command:
        </p>

        <CodeBlock language="bash" title="Linux / macOS (bash/zsh)">
          {`export IL_SECRET_KEY="sk-il-proj-XXX-..."`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
          {`$env:IL_SECRET_KEY = "sk-il-proj-XXX-..."`}
        </CodeBlock>

        <InfoCallout variant="tip">
          For persistent storage across terminal sessions, add the export to your
          shell profile (<code className={styles.mono}>~/.bashrc</code>,{' '}
          <code className={styles.mono}>~/.zshrc</code>) or use your system's
          environment variable settings on Windows. Avoid committing secret keys
          to source control.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="upload-version">
          2. Upload a version
        </h3>
        <CodeBlock language="bash" title="Linux / macOS">
{`export IL_SECRET_KEY="sk-il-proj-XXX-..."
interlucent-uploader --app-id MyApp --file ./MyGame.zip`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
{`$env:IL_SECRET_KEY = "sk-il-proj-XXX-..."
interlucent-uploader --app-id MyApp --file .\\MyGame.zip`}
        </CodeBlock>

        <p className={styles.prose}>
          The uploader connects to the platform, validates your application,
          and begins a multipart upload. You'll see progress output:
        </p>
        <CodeBlock language="text">
{`Authenticating with project key...
Authenticated. Connecting to server...
Connected. Requesting snapshot...
Found app: MyGame (2 versions, quota: 5)
Uploading MyGame.zip (12.4 GB) as "add" to MyGame
 ██████████████████████░░░░  85%  10.5 GB / 12.4 GB  ETA 2m 15s`}
        </CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="cli-arguments">
          Command-Line Arguments
        </h2>
        <p className={styles.prose}>
          All arguments can also be set via environment variables where noted.
        </p>
        <DocTable
          columns={[
            { header: 'Argument', accessor: 'argument', mono: true, nowrap: true },
            { header: 'Short', accessor: 'short', mono: true, nowrap: true },
            { header: 'Env Variable', accessor: 'env', mono: true, nowrap: true },
            { header: 'Default', accessor: 'default' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            {
              argument: '--secret-key',
              short: '-s',
              env: 'IL_SECRET_KEY',
              default: '(required)',
              description: 'Project secret key (sk-il-...)',
            },
            {
              argument: '--app-id',
              short: '-a',
              env: '',
              default: '(required)',
              description: 'Application ID or slug to upload to',
            },
            {
              argument: '--file',
              short: '-f',
              env: '',
              default: '(required)',
              description: 'Path to the zip file to upload',
            },
            {
              argument: '--mode',
              short: '-m',
              env: '',
              default: 'add',
              description: 'Upload mode: add, replace-oldest, replace-default',
            },
            {
              argument: '--api-url',
              short: '',
              env: 'IL_API_URL',
              default: 'https://api.interlucent.ai',
              description: 'Gateway API endpoint URL',
            },
            {
              argument: '--yes',
              short: '-y',
              env: '',
              default: 'false',
              description: 'Skip confirmation prompts (required for replace-default)',
            },
            {
              argument: '--silent',
              short: '',
              env: '',
              default: 'false',
              description: 'Suppress all output except errors',
            },
            {
              argument: '--verbose',
              short: '',
              env: '',
              default: 'false',
              description: 'Per-part logging, retry traces, checksums',
            },
            {
              argument: '--concurrency',
              short: '',
              env: '',
              default: '6',
              description: 'Number of concurrent part uploads',
            },
            {
              argument: '--game-engine',
              short: '',
              env: '',
              default: '(none)',
              description: 'Game engine: unreal or unity',
            },
            {
              argument: '--os',
              short: '',
              env: '',
              default: '(none)',
              description: 'Target OS: windows or linux',
            },
            {
              argument: '--executable-path',
              short: '',
              env: '',
              default: '(none)',
              description: 'Path to the executable within the zip',
            },
            {
              argument: '--cmd-args',
              short: '',
              env: '',
              default: '(none)',
              description: 'Command-line arguments for the application',
            },
            {
              argument: '--env-vars',
              short: '',
              env: '',
              default: '(none)',
              description: 'Environment variables as a JSON string',
            },
          ]}
        />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="upload-modes">
          Upload Modes
        </h2>
        <p className={styles.prose}>
          The <code className={styles.mono}>--mode</code> flag controls how the
          uploader handles version quota limits.
        </p>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>add</div>
            <div className={styles.conceptDef}>
              Upload as a new version. Fails if the application has reached its
              version quota. This is the default mode.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>replace-oldest</div>
            <div className={styles.conceptDef}>
              If quota has room, adds a new version. If quota is full, replaces
              the oldest non-default version. Designed for CI pipelines that
              should always succeed.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>replace-default</div>
            <div className={styles.conceptDef}>
              Replaces the current default version. The new version
              automatically becomes the new default. Requires the{' '}
              <code className={styles.mono}>--yes</code> flag to confirm.
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="practical-examples">
          Practical Examples
        </h2>

        <h3 className={styles.subsectionTitle} id="example-ci-cd">
          CI/CD Pipeline (GitHub Actions)
        </h3>
        <CodeBlock language="yaml" title=".github/workflows/deploy.yml">
{`- name: Upload to Interlucent
  run: |
    interlucent-uploader \\
      --secret-key "\${{ secrets.IL_SECRET_KEY }}" \\
      --app-id MyApp \\
      --file ./dist/MyGame.zip \\
      --mode replace-oldest \\
      --game-engine unreal \\
      --os windows \\
      --yes --silent`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="example-replace-default">
          Replace Default Version
        </h3>
        <p className={styles.prose}>
          Deploy a new build as the default version that users connect to:
        </p>
        <CodeBlock language="bash">
{`interlucent-uploader \\
  --app-id MyApp \\
  --file ./MyGame.zip \\
  --mode replace-default \\
  --yes`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="example-metadata">
          With Application Metadata
        </h3>
        <p className={styles.prose}>
          Specify engine, OS, executable path, and launch arguments:
        </p>
        <CodeBlock language="bash">
{`interlucent-uploader \\
  --app-id MyApp \\
  --file ./MyGame.zip \\
  --game-engine unreal \\
  --os windows \\
  --executable-path "MyGame/Binaries/Win64/MyGame.exe" \\
  --cmd-args "-TestArgument" \\
  --env-vars '{"UE_LOG": "verbose"}'`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="example-env-file">
          Using a .env File
        </h3>
        <CodeBlock language="bash" title="interlucent-uploader.env">
{`export IL_SECRET_KEY="sk-il-proj-XXX-..."
export IL_API_URL="https://api.interlucent.ai"`}
        </CodeBlock>
        <CodeBlock language="bash">
{`source interlucent-uploader.env
interlucent-uploader --app-id MyApp --file ./MyGame.zip`}
        </CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="resume">
          Resume &amp; Retry
        </h2>
        <p className={styles.prose}>
          The uploader is designed for reliability over unreliable networks and
          with large files:
        </p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Automatic Part Retry</span>{' '}
            Failed part uploads are retried with exponential backoff (up to 5
            attempts per part). Network errors and server errors (5xx) trigger
            retries automatically.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>WebSocket Reconnection</span>{' '}
            If the control connection drops mid-upload, the uploader reconnects
            and resumes where it left off — already-uploaded parts are skipped.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Process Restart Resume</span>{' '}
            If the process is killed, re-running the same command detects the
            in-progress upload on the server and resumes it, skipping completed
            parts.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="authentication">
          Authentication
        </h2>
        <p className={styles.prose}>
          The uploader uses your project secret key to authenticate:
        </p>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              The secret key (<code className={styles.mono}>sk-il-...</code>)
              contains an Ed25519 private key. The CLI uses it to sign a
              short-lived admission JWT.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              The admission JWT is exchanged for an access token via the gateway.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              The access token is used for the WebSocket connection. If it
              expires mid-upload, the CLI re-authenticates automatically.
            </span>
          </li>
        </ol>
        <InfoCallout variant="warning">
          Keep your project secret key private. Don't commit it to version
          control or share it in chat. Use environment variables or CI/CD
          secrets for safe storage.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="troubleshooting">
          Troubleshooting
        </h2>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>"App not found"</div>
            <div className={styles.conceptDef}>
              The <code className={styles.mono}>--app-id</code> doesn't match
              any application in your project. Check the ID or slug in the
              console dashboard.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>"Version limit reached"</div>
            <div className={styles.conceptDef}>
              Your application has reached its version quota. Use{' '}
              <code className={styles.mono}>--mode replace-oldest</code> or{' '}
              <code className={styles.mono}>--mode replace-default</code> to
              replace an existing version, or delete a version from the console.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>
              "Refusing to replace default version without --yes"
            </div>
            <div className={styles.conceptDef}>
              The <code className={styles.mono}>replace-default</code> mode
              requires explicit confirmation. Add{' '}
              <code className={styles.mono}>--yes</code> to proceed.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Upload stalls or times out</div>
            <div className={styles.conceptDef}>
              Try reducing concurrency with{' '}
              <code className={styles.mono}>--concurrency 2</code> on slower
              connections. Enable{' '}
              <code className={styles.mono}>--verbose</code> to see per-part
              status.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Authentication errors</div>
            <div className={styles.conceptDef}>
              Verify your secret key is correct and hasn't been revoked. Check
              that <code className={styles.mono}>IL_API_URL</code> points to
              the correct environment.
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
